import React, { useState } from 'react';
import { Skeleton } from 'antd';
import { Link } from "react-router-dom";
import styled from 'styled-components';

import { Campaign } from 'models/CommonType';
import { truncateString } from "utils/helpers";
import './SkeletonItem.css'

const Container = styled.div`
  display: flex;
  padding: 18px;
  margin-bottom: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: var(--color-primary-light);
  }
  @media (max-width: 768px) {
    flex-direction: row;
    padding: 12px;
    margin-bottom: 12px;
  }
`;

const ImageContainer = styled.div`
  padding: 0;
  background-color: #F5F5F5;
  width: 180px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  margin-right: 20px;
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    border-radius: 6px;
    margin-right: 12px;
    margin-bottom: 0;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
`;

const Name = styled.span`
  line-height: 1.3;
  font-size: 1.2rem;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--color-text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 4px;
    line-height: 1.2;
    -webkit-line-clamp: 2;
  }
`;

const Description = styled.span`
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.3;
    -webkit-line-clamp: 2;
  }
`;

interface Props {
  item?: Campaign;
  closeHandler?: () => void;
}

const CampaignItem: React.FC<Props> = ({
  item,
  closeHandler,
}) => {
  const [hasImage, setHasImage] = useState(!!item?.image_url);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setHasImage(false);
    setImageLoaded(false);
  };

  if (!item) {
    return (
      <Container>
        <ImageContainer className='SkeletonImg'>
          <Skeleton.Image active style={{ width: '100%', height: '100%' }} />
        </ImageContainer>
        <TextContainer className='SkeletonText'>
          <Skeleton active paragraph={{ rows: 3 }} />
        </TextContainer>
      </Container>
    );
  }

  const showImage = hasImage && item.image_url;

  return (
    <Link to={`/campaign?id=${item.id}`} onClick={closeHandler}>
      <Container>
        {showImage && (
          <ImageContainer>
            <Image 
              src={item.image_url} 
              alt={item.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ opacity: imageLoaded ? 1 : 0.7 }}
            />
          </ImageContainer>
        )}

        <TextContainer>
          <div>
            <Name>{item.name}</Name>
            <Description>{truncateString(item.description, showImage ? 100 : 150)}</Description>
          </div>
        </TextContainer>
      </Container>
    </Link>
  );
}

export default CampaignItem;
